import { type FC, useCallback, useMemo } from 'react'

import { addMinutes, isBefore } from 'date-fns'
import { useUnit } from 'effector-react'

import CalendarIcon from '@/assets/icons/icon-calendar.svg'
import { CalendarDateTimePicker } from '@/components/ui/calendar/calendar-date-time-picker'
import { useToast } from '@/components/ui/toast'
import { Tooltip } from '@/components/ui/tooltip'
import { $fanId } from '@/models/chattingUI/chats/chats.ts'
import { classNames } from '@/utils/helpers'

interface Props {
  scheduledDate?: Date
  setScheduledDate: (date: Date | undefined) => void
  trigger?: JSX.Element
  open?: boolean
  onOpenChange?: (state: boolean) => void
  disabled?: boolean
  disabledTooltip?: string
}

export const ScheduleMessage: FC<Props> = ({
  scheduledDate,
  setScheduledDate,
  trigger,
  open,
  onOpenChange,
  disabled,
  disabledTooltip,
}) => {
  const toast = useToast()
  const fanId = useUnit($fanId)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const minDate = useMemo(() => new Date(), [fanId])

  const defaultScheduledDate = useMemo(() => {
    if (fanId) {
      return addMinutes(new Date(), 5)
    }
  }, [fanId])

  const handleSave = useCallback(
    (date: Date | undefined) => {
      if (date && isBefore(date, minDate)) {
        toast.warning('Scheduled date must be in the future')

        return
      }

      setScheduledDate(date)
    },
    [minDate, setScheduledDate, toast],
  )

  return (
    <CalendarDateTimePicker
      trigger={
        trigger || (
          <Tooltip
            contentClassName="min-w-max text-title"
            content={
              disabled
                ? (disabledTooltip ??
                  'Scheduled messages are not available for priority messages')
                : 'Scheduled Messages'
            }
            className="flex items-center"
          >
            <CalendarIcon
              className={classNames(
                'h-6 w-6 text-secondary-300',
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer hover:text-primary-500',
              )}
            />
          </Tooltip>
        )
      }
      minDate={minDate}
      title="Scheduled Messages"
      onSave={handleSave}
      date={scheduledDate}
      defaultDate={defaultScheduledDate}
      open={open}
      onOpenChange={onOpenChange}
      disabled={disabled}
    />
  )
}
